import { Modal } from 'components/core-ui/Modal'
import { useFormik } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import { makeCorePatchRequest, makeCorePostRequest, makeCorePutRequest } from 'utils/api'
import { useMutation } from 'react-query'
import { useNotification } from 'hooks'
import { handleQueryError } from 'utils/helpers'
import { FormControlLabel, Grid, Switch } from '@mui/material'
import { Input } from 'components/core-ui/Input'
import { useCountriesQuery } from '../../api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { AsyncAutocomplete } from 'components/core-ui/AsyncAutocomplete'
import { Button } from '@fullscreendigital/carrefour-dex-fe'
import s from 'components/core-ui/Modal/Modal.modules.scss'
import { toLegalEntityPatchPayload, toLegalEntityPayload } from './serializers'

const NewLegalEntityModal = props => {
  const { isModalOpen, closeModal, editItem, setShouldRefreshData } = props

  const [countriesOptions, setCountriesOptions] = useState([])

  const { showNotification } = useNotification()

  const initialValues = useMemo(() => getInitialValues(editItem), [editItem])

  const validationSchema = useMemo(() => getValidationSchema(), [])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async values => {
      mutate(editItem ? toLegalEntityPatchPayload(values) : toLegalEntityPayload(values))
    }
  })

  const mutateLegalEntity = async values => {
    editItem
      ? await makeCorePatchRequest(`/iam/external-partners/legal-entities/${editItem.id}`, values)
      : await makeCorePostRequest('/iam/external-partners/legal-entities', values)
  }

  const { isLoading, mutate } = useMutation(mutateLegalEntity, {
    onSuccess: res => {
      setShouldRefreshData(true)
      closeModal()
      formik.resetForm()
      showNotification({
        message: `Legal entity was ${editItem?.id ? 'edited' : 'created'} successfully.`,
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        `There was an error with ${
          editItem?.id ? 'editing the legal entity.' : 'adding the legal entity.'
        }`,
        showNotification
      )
  })

  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    refetch: refetchCountries
  } = useCountriesQuery(
    showNotification,
    toTableGETUrl({
      page: 0,
      rowsPerPage: 1000,
      url: '/iam/external-partners/countries'
    })
  )

  useEffect(() => {
    if (countriesData) {
      setCountriesOptions(
        countriesData.map(country => ({
          label: country.name,
          value: country.id
        }))
      )
    }
  }, [countriesData])

  const cancelModal = () => {
    closeModal()
    formik.resetForm()
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle="Legal Entity"
      modalWidth="50%"
    >
      <form>
        <Grid container spacing={4} style={{ marginBottom: '48px' }}>
          <Grid item xs={12}>
            <Input
              type="text"
              variant="outlined"
              name="name"
              label="Legal entity name (ANAF registered name for Romanian companies)"
              width="100%"
              value={formik.values.name}
              onChange={event =>
                formik.setFieldValue('name', (event.target as HTMLInputElement).value)
              }
              required
              error={!!formik.errors.name}
              helperText={formik.errors.name as string}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Input
                  type="text"
                  variant="outlined"
                  name="code"
                  label="Fiscal identification code (CIF)"
                  width="100%"
                  value={formik.values.code}
                  onChange={formik.handleChange}
                  required
                  disabled={!!editItem}
                  error={!!formik.errors.code}
                  helperText={formik.errors.code as string}
                />
              </Grid>
              <Grid item xs={6}>
                <AsyncAutocomplete
                  variant="outlined"
                  name="country"
                  label="Legal entity country"
                  value={formik.values.country}
                  options={countriesOptions}
                  onChange={value => formik.setFieldValue('country', value)}
                  required
                  disabled={!!editItem}
                  error={!!formik.errors.country}
                  helperText={
                    typeof formik.errors.country === 'object' &&
                    !Array.isArray(formik.errors.country)
                      ? (formik.errors.country?.value as string)
                      : ''
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          {!!editItem && (
            <Grid item xs={12}>
              <span style={{ display: 'inline-block', lineHeight: '38px', marginRight: '12px' }}>
                Status:
              </span>
              <FormControlLabel
                control={
                  <Switch
                    name="isEnabled"
                    checked={formik.values.isEnabled}
                    onChange={event => formik.setFieldValue('isEnabled', event.target.checked)}
                    onBlur={formik.handleBlur}
                  />
                }
                label="Enabled"
              />
              <span
                style={{
                  color: 'rgba(0, 0, 0, 0.38)',
                  display: 'inline-block',
                  lineHeight: '38px'
                }}
              >
                (can be disabled if no active assigned partners)
              </span>
            </Grid>
          )}
          <Grid item xs={12} className={s.modalButtons}>
            <Button variant="outlined" label="Cancel" onClick={closeModal} />
            <Button
              variant="contained"
              label="Save"
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
            />
          </Grid>
        </Grid>
      </form>
    </Modal>
  )
}

export default NewLegalEntityModal
